/**
 * inline_edit_controller.js — click-to-edit the description in place (Stimulus).
 *
 * Backs components/detail/entity_description.html on quote / job / invoice
 * detail. The pencil swaps the read-only block for the editor; Save POSTs
 * through window.TurboEditModal and the update endpoint re-renders this whole
 * partial as a Turbo Stream replace — which swaps back to display mode and
 * reconnects a fresh controller. Cancel restores the content the editor opened
 * with.
 *
 * The editor is NOT this controller's concern. macros/rich_description.html
 * nests a rich-description controller that owns Tiptap, formatting and images,
 * and mirrors its markup into a hidden textarea[name="description_html"] inside
 * this form. So the save path here is the same as it ever was — serialize the
 * form — and this controller only reads that textarea to guard an empty save
 * and to remember what Cancel should restore. The server derives the plain
 * `description` column from the submitted markup (RichDescriptionService).
 *
 * Config (data-* on the controller root):
 *   data-inline-edit-url          POST target (the entity's update endpoint)
 *   data-inline-edit-entity-type  'quote' | 'job' | 'invoice'
 *
 * Markup contract inside the root:
 *   .inline-edit-display   read-only description
 *   .inline-edit-form      <form data-action="submit->inline-edit#save">
 *   .rich-description      the nested editor (exposes chippieSetHTML)
 *   textarea[name=description_html]
 *
 * The partial omits every field except the description, so the endpoint must
 * preserve absent fields — see the *_submitted sentinels in
 * application/web/views/{quotes,jobs,invoices}.py. A server-side validation
 * rejection targets the edit modal's (hidden) error region, so it surfaces here
 * as an error toast instead and the editor stays open for retry.
 *
 * One live editor at a time: participates in the shared coordination protocol
 * (inline_editor_coordination.js) — requestOpen() gates edit(), and the
 * bindEditorGuards hooks commit/close/veto this draft when a peer opens or
 * the page navigates. Emptied is the only vetoing state (NOT NULL column).
 */

import { Controller } from "@hotwired/stimulus";
import { bindEditorGuards, releaseHeldUpdate, requestOpen } from "./inline_editor_coordination.js";

export default class extends Controller {
        connect() {
            this.url = this.element.dataset.inlineEditUrl;
            this.entityType = this.element.dataset.inlineEditEntityType || 'item';
            this.display = this.element.querySelector('.inline-edit-display');
            this.form = this.element.querySelector('.inline-edit-form');
            this.field = this.element.querySelector('.rich-description');
            this.textarea = this.form
                ? this.form.querySelector('textarea[name="description_html"]')
                : null;
            this.original = this.textarea ? this.textarea.value : '';

            this._unbindGuards = bindEditorGuards(this.element, {
                isOpen: () => !!this.form && !this.form.classList.contains('d-none'),
                // A photo still uploading is an edit the markup does not show
                // yet; closing on it would drop the photo from the description.
                isDirty: () => (!!this.textarea && this.textarea.value !== this.original)
                    || !!(this.field && this.field.chippieUploading && this.field.chippieUploading()),
                // description is NOT NULL — an emptied editor cannot commit.
                canCommit: () => !!this.textarea.value,
                commit: () => this.save(),
                close: () => this._showDisplay(),
                veto: () => {
                    this._focusEditor();
                    if (typeof window.showNotification === 'function') {
                        window.showNotification('Finish or cancel the description first.', 'error');
                    }
                },
            });
        }

        disconnect() {
            if (this._unbindGuards) {
                this._unbindGuards();
                this._unbindGuards = null;
            }
        }

        edit() {
            if (!this.form) return;
            if (!requestOpen(this.element)) return;
            this.display.classList.add('d-none');
            this.form.classList.remove('d-none');
            this._focusEditor();
        }

        cancel() {
            if (this.field && this.field.chippieSetHTML) {
                this.field.chippieSetHTML(this.original);
            } else if (this.textarea) {
                this.textarea.value = this.original;
            }
            this._showDisplay();
            releaseHeldUpdate(this.element);
        }

        async save(event) {
            if (event) event.preventDefault();
            if (!this.textarea || !window.TurboEditModal) return;

            // description is NOT NULL and feeds search and embeddings, so an
            // empty editor must not be submitted. The nested controller mirrors
            // Tiptap's empty document as '' rather than "<p></p>" precisely so
            // this check is a plain truthiness test.
            if (!this.textarea.value) {
                this._focusEditor();
                return;
            }

            // No modalId: TurboEditModal applies the returned stream, which
            // replaces this.element back to display mode. On error it shows a
            // toast and the editor stays open for retry. A formError stream
            // targets the modal's hidden error region — invisible here — so
            // surface it as a toast ourselves.
            const result = await window.TurboEditModal.submit(this.form, {
                entityType: this.entityType,
                url: this.url,
                onError: (err) => {
                    if (err && err.formError && typeof window.showNotification === 'function') {
                        window.showNotification(
                            'That value was not accepted. Please check it and try again.', 'error');
                    }
                },
            });
            // Queued offline: no stream comes back to swap the editor out, so
            // close it here; the description's offline slot shows the pending text.
            if (result && result.queued) {
                this._showDisplay();
                releaseHeldUpdate(this.element);
            }
        }

        _focusEditor() {
            const editable = this.field
                ? this.field.querySelector('[contenteditable="true"]') : null;
            if (editable) editable.focus();
        }

        _showDisplay() {
            if (this.form) this.form.classList.add('d-none');
            if (this.display) this.display.classList.remove('d-none');
        }
}
